import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../App';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = `${BACKEND_URL}/api`;

const RegistrationFlowNew = () => {
  const navigate = useNavigate();
  const { token, user } = useAuth();
  
  const [currentStep, setCurrentStep] = useState(1); // 1-6
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [currentUserData, setCurrentUserData] = useState(null);
  
  // Master data
  const [seriesList, setSeriesList] = useState([]);
  const [jenisKompetisi] = useState(['Futsal', 'Dance', 'Skill Challenge']);
  
  // Selection
  const [selectedSeries, setSelectedSeries] = useState(null);
  const [selectedJenis, setSelectedJenis] = useState('');
  const [availableEvents, setAvailableEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [selectedRole, setSelectedRole] = useState(''); // kapten, guru
  
  // Form Data
  const [formData, setFormData] = useState({
    peran: '',
    nama_sekolah: '',
    nama_tim_sekolah: '',
    kategori_skill: '', // KU 10 / KU 12 for skill only
    prestasi: [{ nama: '', tahun: '', sertifikat_url: '' }], // 3 prestasi
    media_sosial_tim: '',
    media_sosial_supporter: '',
    media_sosial_crew: '',
    media_sosial_sekolah: ''
  });

  useEffect(() => {
    fetchSeries();
    fetchCurrentUser();
  }, []);

  useEffect(() => {
    if (selectedSeries && selectedJenis) {
      fetchFilteredEvents();
    }
  }, [selectedSeries, selectedJenis]);

  const fetchCurrentUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCurrentUserData(response.data);
      // Set nama sekolah dari user data
      if (response.data.nama_sekolah) {
        setFormData(prev => ({ ...prev, nama_sekolah: response.data.nama_sekolah }));
      }
    } catch (error) {
      console.error('Error fetching user data:', error);
    }
  };

  const fetchSeries = async () => {
    try {
      const response = await axios.get(`${API}/series?aktif=true`);
      setSeriesList(response.data);
    } catch (error) {
      toast.error('Gagal memuat data series');
    }
  };

  const fetchFilteredEvents = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/events?status_open=true`);
      
      // Filter by series and jenis (from kategori)
      const filtered = response.data.filter(event => {
        const matchSeries = event.m_series_id === selectedSeries.id;
        const matchJenis = event.kategori && event.kategori.toLowerCase().includes(selectedJenis.toLowerCase());
        return matchSeries && matchJenis;
      });
      
      setAvailableEvents(filtered);
    } catch (error) {
      toast.error('Gagal memuat events');
    } finally {
      setLoading(false);
    }
  };

  const handleNext = () => {
    // Validation
    if (currentStep === 1 && !selectedSeries) {
      toast.error('Pilih kota series terlebih dahulu');
      return;
    }
    if (currentStep === 3 && !selectedJenis) {
      toast.error('Pilih jenis kompetisi terlebih dahulu');
      return;
    }
    if (currentStep === 4 && !selectedEvent) {
      toast.error('Pilih event terlebih dahulu');
      return;
    }
    if (currentStep === 5 && !selectedRole) {
      toast.error('Pilih role terlebih dahulu');
      return;
    }
    
    setCurrentStep(prev => Math.min(prev + 1, 6));
  };

  const handlePrev = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  const handleFileUpload = async (e, prestasiIndex = null) => {
    const file = e.target.files[0];
    if (!file) return;

    const formDataUpload = new FormData();
    formDataUpload.append('file', file);

    try {
      toast('Uploading...');
      const response = await axios.post(`${API}/upload/document`, formDataUpload, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      if (prestasiIndex !== null) {
        const newPrestasi = [...formData.prestasi];
        newPrestasi[prestasiIndex].sertifikat_url = response.data.url;
        setFormData({ ...formData, prestasi: newPrestasi });
      }
      
      toast.success('File berhasil diupload');
    } catch (error) {
      toast.error('Gagal upload file');
    }
  };

  const addPrestasi = () => {
    if (formData.prestasi.length >= 3) {
      toast.error('Maksimal 3 prestasi');
      return;
    }
    setFormData({
      ...formData,
      prestasi: [...formData.prestasi, { nama: '', tahun: '', sertifikat_url: '' }]
    });
  };

  const removePrestasi = (index) => {
    const newPrestasi = formData.prestasi.filter((_, i) => i !== index);
    setFormData({ ...formData, prestasi: newPrestasi });
  };

  const updatePrestasi = (index, field, value) => {
    const newPrestasi = [...formData.prestasi];
    newPrestasi[index][field] = value;
    setFormData({ ...formData, prestasi: newPrestasi });
  };

  const handleSubmit = async () => {
    // Validation
    const jenis = selectedJenis.toLowerCase();
    
    if (!formData.nama_tim_sekolah) {
      toast.error('Nama tim sekolah wajib diisi');
      return;
    }

    // Validasi prestasi untuk semua jenis kompetisi
    if (formData.prestasi.length < 3) {
      toast.error('Harap isi 3 prestasi terakhir');
      return;
    }

    const allPrestasiComplete = formData.prestasi.every(p => p.nama && p.tahun && p.sertifikat_url);
    if (!allPrestasiComplete) {
      toast.error('Lengkapi semua data prestasi dan upload sertifikat');
      return;
    }

    if (jenis === 'skill' && !formData.kategori_skill) {
      toast.error('Pilih kategori skill (KU 10 atau KU 12)');
      return;
    }

    setSubmitting(true);

    try {
      // Get series from event
      if (!selectedEvent.m_series_id) {
        toast.error('Event belum memiliki series yang terkait');
        return;
      }

      // Get kompetisi from series, filter by jenis
      const kompetisiRes = await axios.get(`${API}/series/${selectedEvent.m_series_id}/kompetisi`);
      const allKompetisi = kompetisiRes.data;
      
      // Find kompetisi that matches selectedJenis
      const matchingKompetisi = allKompetisi.filter(k => 
        k.jenis.toLowerCase() === selectedJenis.toLowerCase()
      );
      
      if (matchingKompetisi.length === 0) {
        toast.error(`Kompetisi ${selectedJenis} belum tersedia untuk series ini`);
        return;
      }
      
      // Use first matching kompetisi
      const kompetisi = matchingKompetisi[0];

      const registrationData = {
        m_event_id: selectedEvent.id,
        m_series_id: selectedEvent.m_series_id,
        m_kompetisi_id: kompetisi.id,
        role_kompetisi: selectedRole,
        data_utama: {
          peran: selectedRole,
          nama_sekolah: formData.nama_sekolah,
          nama_tim_sekolah: formData.nama_tim_sekolah,
          kategori_skill: formData.kategori_skill,
          prestasi: formData.prestasi,
          media_sosial_tim: formData.media_sosial_tim,
          media_sosial_supporter: formData.media_sosial_supporter,
          media_sosial_crew: formData.media_sosial_crew,
          media_sosial_sekolah: formData.media_sosial_sekolah
        }
      };

      await axios.post(`${API}/registrations`, registrationData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Registrasi berhasil! Data Anda sedang dalam proses validasi.');
      navigate('/home');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal submit registrasi');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 pb-24">
      {/* Header */}
      <header className="bg-gradient-ltb-r text-white py-6 px-6 shadow-lg">
        <div className="max-w-7xl mx-auto">
          <h1 className="text-3xl font-bold">Registrasi Event</h1>
          <p className="text-white/80 mt-1">Liga Tendang Bola Plus</p>
        </div>
      </header>

      {/* Progress Indicator */}
      <div className="max-w-4xl mx-auto px-6 py-8 pb-24">
        <div className="flex items-center justify-center mb-8">
          {['Booklet', 'Kota', 'Jenis', 'Event', 'Role', 'Data'].map((step, index) => (
            <React.Fragment key={index}>
              <div className="flex flex-col items-center">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm ${
                  currentStep === index + 1 
                    ? 'bg-ltb-bright text-white' 
                    : currentStep > index + 1
                    ? 'bg-green-500 text-white'
                    : 'bg-gray-300 text-gray-600'
                }`}>
                  {currentStep > index + 1 ? '✓' : index + 1}
                </div>
                <span className={`text-xs mt-1 ${
                  currentStep === index + 1 ? 'text-ltb-bright font-semibold' : 'text-gray-500'
                }`}>
                  {step}
                </span>
              </div>
              {index < 5 && (
                <div className={`w-12 h-1 mx-1 ${
                  currentStep > index + 1 ? 'bg-green-500' : 'bg-gray-300'
                }`} />
              )}
            </React.Fragment>
          ))}
        </div>

        {/* Content */}
        <div className="bg-white rounded-lg shadow-lg p-8">
          {currentStep === 1 && <Step1Series seriesList={seriesList} selectedSeries={selectedSeries} setSelectedSeries={setSelectedSeries} />}
          {currentStep === 2 && <Step2Booklet selectedSeries={selectedSeries} />}
          {currentStep === 3 && <Step3Jenis jenisKompetisi={jenisKompetisi} selectedJenis={selectedJenis} setSelectedJenis={setSelectedJenis} />}
          {currentStep === 4 && <Step4Event loading={loading} events={availableEvents} selectedEvent={selectedEvent} setSelectedEvent={setSelectedEvent} />}
          {currentStep === 5 && <Step5Role selectedRole={selectedRole} setSelectedRole={setSelectedRole} selectedJenis={selectedJenis} />}
          {currentStep === 6 && (
            <Step6Data 
              selectedRole={selectedRole}
              selectedJenis={selectedJenis}
              formData={formData}
              setFormData={setFormData}
              handleFileUpload={handleFileUpload}
              addPrestasi={addPrestasi}
              removePrestasi={removePrestasi}
              updatePrestasi={updatePrestasi}
            />
          )}
        </div>

        {/* Navigation */}
        <div className="flex justify-between mt-8 mb-8">
          {currentStep > 1 && (
            <button
              onClick={handlePrev}
              className="px-6 py-3 border-2 border-ltb-bright text-ltb-bright rounded-lg font-semibold hover:bg-ltb-bright hover:text-white transition-all"
            >
              ← Kembali
            </button>
          )}
          
          <div className="ml-auto">
            {currentStep < 6 ? (
              <button
                onClick={handleNext}
                className="px-6 py-3 bg-gradient-ltb-r text-white rounded-lg font-semibold hover:shadow-xl transition-all"
              >
                Berikutnya →
              </button>
            ) : (
              <button
                onClick={handleSubmit}
                disabled={submitting}
                className="px-8 py-3 bg-gradient-ltb-r text-white rounded-lg font-semibold hover:shadow-xl transition-all disabled:opacity-50"
              >
                {submitting ? 'Submitting...' : 'Submit Registrasi'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Step Components

// Step 1: Pilih Kota Series
const Step1Series = ({ seriesList, selectedSeries, setSelectedSeries }) => (
  <div className="space-y-4">
    <h2 className="text-2xl font-bold text-gray-800">Pilih Kota Series</h2>
    <p className="text-gray-600">Pilih kota series untuk event yang ingin Anda ikuti</p>
    
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
      {seriesList.map((series) => (
        <button
          key={series.id}
          onClick={() => setSelectedSeries(series)}
          className={`p-6 rounded-lg border-2 transition-all text-left ${
            selectedSeries?.id === series.id
              ? 'border-ltb-bright bg-ltb-bright/10'
              : 'border-gray-300 hover:border-ltb-bright hover:bg-gray-50'
          }`}
        >
          <h3 className="text-xl font-bold text-gray-800">{series.nama_series}</h3>
          {series.deskripsi && (
            <p className="text-gray-600 mt-2 text-sm">{series.deskripsi}</p>
          )}
          {series.booklet_url && (
            <p className="text-sm text-blue-600 mt-2">📄 Booklet tersedia</p>
          )}
        </button>
      ))}
    </div>
    
    {seriesList.length === 0 && (
      <div className="text-center py-8 text-gray-500">
        Belum ada series yang tersedia
      </div>
    )}
  </div>
);

// Step 2: Preview Booklet
const Step2Booklet = ({ selectedSeries }) => (
  <div className="space-y-6">
    <div className="text-center">
      <h2 className="text-2xl font-bold text-gray-800">Booklet & Regulasi</h2>
      <p className="text-gray-600 mt-2">{selectedSeries?.nama_series}</p>
    </div>
    
    {selectedSeries?.booklet_url ? (
      <div className="space-y-4">
        {/* PDF Preview */}
        <div className="border-2 border-gray-300 rounded-lg overflow-hidden">
          <iframe
            src={selectedSeries.booklet_url}
            className="w-full h-[500px]"
            title="Booklet Preview"
          />
        </div>
        
        {/* Download Button */}
        <div className="text-center">
          <a
            href={selectedSeries.booklet_url}
            download
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block bg-ltb-bright text-white px-6 py-3 rounded-lg hover:shadow-lg transition-all"
          >
            📥 Download Booklet
          </a>
        </div>
      </div>
    ) : (
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 text-center">
        <p className="text-yellow-800">Booklet belum tersedia untuk series ini</p>
      </div>
    )}
  </div>
);

// Step 3: Pilih Jenis Kompetisi (unchanged)

const Step3Jenis = ({ jenisKompetisi, selectedJenis, setSelectedJenis }) => (
  <div className="space-y-4">
    <h2 className="text-2xl font-bold text-gray-800">Pilih Jenis Kompetisi</h2>
    <p className="text-gray-600">Pilih jenis kompetisi yang ingin Anda ikuti</p>
    
    <div className="grid gap-4">
      {jenisKompetisi.map((jenis) => (
        <button
          key={jenis}
          onClick={() => setSelectedJenis(jenis)}
          className={`p-6 border-2 rounded-lg text-left transition-all ${
            selectedJenis === jenis
              ? 'border-ltb-bright bg-ltb-bright/10'
              : 'border-gray-300 hover:border-ltb-bright'
          }`}
        >
          <h3 className="text-xl font-bold text-gray-800">{jenis}</h3>
        </button>
      ))}
    </div>
  </div>
);

const Step4Event = ({ loading, events, selectedEvent, setSelectedEvent }) => (
  <div className="space-y-4">
    <h2 className="text-2xl font-bold text-gray-800">Pilih Event</h2>
    <p className="text-gray-600">Event yang tersedia berdasarkan kota dan jenis kompetisi yang dipilih</p>
    
    {loading ? (
      <div className="text-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-ltb-bright mx-auto"></div>
        <p className="mt-4 text-gray-600">Loading events...</p>
      </div>
    ) : events.length === 0 ? (
      <div className="border-2 border-dashed border-gray-300 rounded-lg p-12 text-center">
        <p className="text-gray-500">Tidak ada event yang tersedia untuk kota dan jenis kompetisi yang dipilih</p>
      </div>
    ) : (
      <div className="grid gap-4">
        {events.map((event) => (
          <button
            key={event.id}
            onClick={() => setSelectedEvent(event)}
            className={`p-6 border-2 rounded-lg text-left transition-all ${
              selectedEvent?.id === event.id
                ? 'border-ltb-bright bg-ltb-bright/10'
                : 'border-gray-300 hover:border-ltb-bright'
            }`}
          >
            <h3 className="text-xl font-bold text-gray-800">{event.nama}</h3>
            <p className="text-gray-600 mt-2">{event.description}</p>
            <div className="flex items-center text-sm text-gray-500 mt-2">
              <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              {new Date(event.tgl_event_mulai).toLocaleDateString('id-ID')} - {new Date(event.tgl_event_selesai).toLocaleDateString('id-ID')}
            </div>
          </button>
        ))}
      </div>
    )}
  </div>
);

const Step5Role = ({ selectedRole, setSelectedRole, selectedJenis }) => {
  const isSkill = selectedJenis.toLowerCase().includes('skill');
  
  return (
    <div className="space-y-4">
      <h2 className="text-2xl font-bold text-gray-800">Pilih Role</h2>
      <p className="text-gray-600">Pilih role Anda dalam kompetisi ini</p>
      
      <div className={`grid ${isSkill ? 'grid-cols-1' : 'grid-cols-2'} gap-4 max-w-md mx-auto`}>
        {!isSkill && (
          <button
            onClick={() => setSelectedRole('kapten')}
            className={`p-6 border-2 rounded-lg text-center transition-all ${
              selectedRole === 'kapten'
                ? 'border-ltb-bright bg-ltb-bright text-white'
                : 'border-gray-300 hover:border-ltb-bright'
            }`}
          >
            <div className="text-4xl mb-2">👨‍✈️</div>
            <div className="font-bold text-lg">Kapten</div>
          </button>
        )}
        
        <button
          onClick={() => setSelectedRole('guru')}
          className={`p-6 border-2 rounded-lg text-center transition-all ${
            selectedRole === 'guru'
              ? 'border-ltb-bright bg-ltb-bright text-white'
              : 'border-gray-300 hover:border-ltb-bright'
          }`}
        >
          <div className="text-4xl mb-2">👨‍🏫</div>
          <div className="font-bold text-lg">Guru</div>
        </button>
      </div>
    </div>
  );
};

const Step6Data = ({ 
  selectedRole, 
  selectedJenis, 
  formData, 
  setFormData,
  handleFileUpload,
  addPrestasi,
  removePrestasi,
  updatePrestasi
}) => {
  const jenis = selectedJenis.toLowerCase();
  const isFutsal = jenis.includes('futsal');
  const isDance = jenis.includes('dance');
  const isSkill = jenis.includes('skill');

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold text-gray-800">Isi Data Registrasi</h2>
      
      {/* Peran */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Peran <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          value={selectedRole}
          readOnly
          className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-100 capitalize"
        />
      </div>

      {/* Nama Sekolah */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Nama Sekolah <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          value={formData.nama_sekolah}
          onChange={(e) => setFormData({ ...formData, nama_sekolah: e.target.value })}
          className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-100"
          readOnly={!isSkill}
        />
      </div>

      {/* Nama Tim Sekolah */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Nama Tim Sekolah <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          value={formData.nama_tim_sekolah}
          onChange={(e) => setFormData({ ...formData, nama_tim_sekolah: e.target.value })}
          className="w-full px-4 py-2 border border-gray-300 rounded-lg"
          placeholder="Masukkan nama tim sekolah"
        />
      </div>

      {/* Kategori Skill (KU 10/KU 12) - Only for Skill */}
      {isSkill && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Kategori <span className="text-red-500">*</span>
          </label>
          <select
            value={formData.kategori_skill}
            onChange={(e) => setFormData({ ...formData, kategori_skill: e.target.value })}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg"
          >
            <option value="">Pilih kategori</option>
            <option value="KU 10">KU 10</option>
            <option value="KU 12">KU 12</option>
          </select>
        </div>
      )}

      {/* 3 Prestasi Terakhir - Untuk Semua Jenis Kompetisi */}
      <div className="space-y-4">
        <div className="flex justify-between items-center">
          <label className="block text-sm font-medium text-gray-700">
            3 Prestasi Terakhir <span className="text-red-500">*</span>
          </label>
          {formData.prestasi.length < 3 && (
            <button
              onClick={addPrestasi}
              className="text-sm bg-ltb-bright text-white px-4 py-1 rounded-lg hover:shadow-lg transition-all"
            >
              + Tambah
            </button>
          )}
        </div>
          
          {formData.prestasi.map((prestasi, index) => (
            <div key={index} className="border border-gray-300 rounded-lg p-4 space-y-3">
              <div className="flex justify-between items-center">
                <h4 className="font-semibold text-gray-700">Prestasi {index + 1}</h4>
                {formData.prestasi.length > 1 && (
                  <button
                    onClick={() => removePrestasi(index)}
                    className="text-sm text-red-600 hover:text-red-700"
                  >
                    Hapus
                  </button>
                )}
              </div>
              
              <input
                type="text"
                value={prestasi.nama}
                onChange={(e) => updatePrestasi(index, 'nama', e.target.value)}
                placeholder="Nama prestasi"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg"
              />
              
              <input
                type="text"
                value={prestasi.tahun}
                onChange={(e) => updatePrestasi(index, 'tahun', e.target.value)}
                placeholder="Tahun"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg"
              />
              
              <div>
                <label className="block text-sm text-gray-600 mb-1">Sertifikat/Foto Prestasi</label>
                <input
                  type="file"
                  accept="image/*,application/pdf"
                  onChange={(e) => handleFileUpload(e, index)}
                  className="w-full text-sm"
                />
                {prestasi.sertifikat_url && (
                  <p className="text-sm text-green-600 mt-1">✓ File uploaded</p>
                )}
              </div>
            </div>
          ))}
        </div>

      {/* Media Sosial Tim Futsal */}
      {isFutsal && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Media Sosial Tim Futsal <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={formData.media_sosial_tim}
            onChange={(e) => setFormData({ ...formData, media_sosial_tim: e.target.value })}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg"
            placeholder="@instagram_tim atau link"
          />
        </div>
      )}

      {/* Media Sosial Tim Supporter - Futsal only */}
      {isFutsal && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Media Sosial Tim Supporter <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={formData.media_sosial_supporter}
            onChange={(e) => setFormData({ ...formData, media_sosial_supporter: e.target.value })}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg"
            placeholder="@instagram_supporter atau link"
          />
        </div>
      )}

      {/* Media Sosial Crew Dance - Dance only */}
      {isDance && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Media Sosial Crew Dance <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={formData.media_sosial_crew}
            onChange={(e) => setFormData({ ...formData, media_sosial_crew: e.target.value })}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg"
            placeholder="@instagram_crew atau link"
          />
        </div>
      )}

      {/* Media Sosial Tim - Skill only */}
      {isSkill && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Media Sosial Tim <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={formData.media_sosial_tim}
            onChange={(e) => setFormData({ ...formData, media_sosial_tim: e.target.value })}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg"
            placeholder="@instagram_tim atau link"
          />
        </div>
      )}

      {/* Media Sosial Sekolah - Skill only */}
      {isSkill && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Media Sosial Sekolah <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={formData.media_sosial_sekolah}
            onChange={(e) => setFormData({ ...formData, media_sosial_sekolah: e.target.value })}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg"
            placeholder="@instagram_sekolah atau link"
          />
        </div>
      )}
    </div>
  );
};

export default RegistrationFlowNew;
